// Node contract for the composable TexAvatars inference graph (DAG of camera/encoder/decoder/branch/lift nodes).

import type * as ort from "../inference/ort";
import type { LiftInput } from "../inference/lift";
import type * as THREE from "three";

// The two mutable per-frame drivers; a node's dirtyOn() picks the minimum recompute set.
export type DirtySource = "rig" | "camera";

// A graph-edge value: CPU Float32Array or a live gpu-buffer ORT tensor on the shared device.
export type NodeTensor =
  | { readonly loc: "cpu"; readonly data: Float32Array; readonly dims: readonly number[] }
  | { readonly loc: "gpu-buffer"; readonly tensor: ort.Tensor; readonly dims: readonly number[] };

export const isGpuTensor = (v: NodeTensor): boolean => v.loc === "gpu-buffer";

// Adapt a NodeTensor to the lifter's LiftInput union.
export function nodeTensorToLiftInput(v: NodeTensor): LiftInput {
  if (v.loc === "cpu") return v.data;
  const n = v.dims.reduce((a, b) => a * b, 1);
  return { gpuBuffer: (v.tensor as unknown as { gpuBuffer: GPUBuffer }).gpuBuffer, byteLength: n * 4 };
}

// Inverse adapter: wrap a runner-path LiftInput as a graph-edge NodeTensor (dims must match its byteLength).
export function liftInputToNodeTensor(v: LiftInput, dims: readonly number[]): NodeTensor {
  if (v instanceof Float32Array) return { loc: "cpu", data: v, dims };
  return { loc: "gpu-buffer", tensor: { gpuBuffer: v.gpuBuffer } as unknown as ort.Tensor, dims };
}

export interface PortSpec {
  readonly name: string;
}

// `from` is an upstream node id, or the reserved sources "__rig__" / "__camera__" (bound to FrameInputs).
export interface EdgeBinding {
  readonly from: string;
  readonly fromPort: string;
  readonly toPort: string;
}

export interface FrameInputs {
  readonly rig: Float32Array | null;
  readonly camera: THREE.PerspectiveCamera;
  readonly rigEpoch: number;
  readonly cameraEpoch: number;
}

export interface NodeContext {
  readonly ort: typeof ort;
  readonly uvRes: number;
  readonly rigDim: number;
  // Shared ORT WebGPU device on the gpu-buffer path, else null.
  readonly sharedDevice: GPUDevice | null;
  readonly gpuBufferIo: boolean;
}

// Per-node asset bytes/urls resolved by the loader, keyed by a node-local name ("fp32"/"fp16"/"latent").
export interface NodeAssets {
  readonly [key: string]: ArrayBuffer | Uint8Array | string | undefined;
}

// Stateful, long-lived compute node; owns its session/GPU resources. The runtime calls evaluate only when dirty.
export interface Node {
  readonly id: string;
  readonly type: string;
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[];
  readonly bindings: readonly EdgeBinding[];

  init(ctx: NodeContext, assets?: NodeAssets): Promise<void>;

  // `inbound` holds upstream outputs keyed by THIS node's input-port name; returns outputs keyed by output-port name.
  evaluate(
    frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>>;

  dirtyOn(): readonly DirtySource[];

  dispose(): void;
}
